/**
 * ProjectHeader — Title + domain/stage badges inline, institution if available
 * Sprint 8 — S8-14 Integration (updated: removed Added by/Created, badges inline with title)
 */

import type { ProjectDetailProject } from '../../types'
import { getDomainConfig } from '../../lib/domainRegistry'
import { getPipelineStageConfig } from '../../lib/pipelineRegistry'

export interface ProjectHeaderProps {
  project: ProjectDetailProject
  onClose: () => void
}

export function ProjectHeader({ project, onClose }: ProjectHeaderProps) {
  const domainConfig = getDomainConfig(project.domain || 'biotech')
  const stageConfig = getPipelineStageConfig(project.pipeline_stage)

  // Institution name
  const institutionName = project.institution?.name || ''

  return (
    <div className="border-b border-border-standard px-6 py-4">
      {/* Title + Badges + Close button — single row */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {/* Project title */}
          <h1 className="text-[20px] font-semibold text-text-primary leading-tight truncate">
            {project.title}
          </h1>

          {/* Domain badge */}
          {domainConfig && (
            <span
              className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap flex-shrink-0"
              style={{
                backgroundColor: `${domainConfig.color}20`,
                color: domainConfig.color,
              }}
            >
              {domainConfig.label}
            </span>
          )}

          {/* Stage badge (red) */}
          {stageConfig && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-primary-red/15 text-primary-red whitespace-nowrap flex-shrink-0">
              {stageConfig.label}
            </span>
          )}
        </div>

        {/* Close button (X) top-right */}
        <button
          onClick={onClose}
          className="flex-shrink-0 text-text-tertiary hover:text-text-secondary transition-colors"
          aria-label="Close project detail"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Institution — only if available */}
      {institutionName && (
        <div className="mt-1.5 text-sm text-text-secondary">
          {institutionName}
        </div>
      )}
    </div>
  )
}

export default ProjectHeader
